/* eslint-disable import/prefer-default-export */

// Runtime config: SPAs read these from /env-config.js (window._env_) at
// runtime, NOT from build-time process.env (which is empty in the deployed
// bundle). Mirrors the getEnv pattern in utils/agenticApiClient.js.
const getEnv = (key) => window._env_?.[key] ?? process.env[key];

const getAgenticBaseUrl = () => {
  const raw = (getEnv('REACT_APP_AGENTIC_API_BASE_URL') || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '');
  return `https://${raw.replace(/\/+$/, '')}`;
};

const getAgenticCode = () => (getEnv('REACT_APP_AGENTIC_API_CODE')
  || getEnv('REACT_APP_AGENTIC_API_FUNCTION_KEY')
  || '').trim();

const tryExtractLegacyDocumentParams = (rawUrl) => {
  if (!rawUrl) return null;
  const cleaned = String(rawUrl).replace(/&amp;/g, '&').trim();
  const queryIndex = cleaned.indexOf('?');
  const query = queryIndex >= 0 ? cleaned.slice(queryIndex + 1) : '';
  if (!query) return null;

  const params = new URLSearchParams(query);
  const caseId = params.get('case_id') || params.get('caseId');
  const filename = params.get('filename') || params.get('fileName');

  if (!caseId || !filename) return null;
  return { caseId, filename };
};

export function normalizeDocumentAgentResponse(apiResponse) {
  const data = apiResponse?.data?.output_text
    || apiResponse?.data
    || apiResponse?.output_text
    || null;

  // ---------- FALLBACK ----------
  if (!data) {
    return {
      type: "run_summary",
      summary: apiResponse?.response,
      counts: {},
      matches: [],
      clusters: [],
      caseRow: null,
      documentRows: [],
      outputText: apiResponse,
      agent: "DocumentProcessingAgent",
      intent: apiResponse?.output_text?.intent ?? undefined,
    };
  }

  // ---------- ERROR ----------
  if (apiResponse?.status === "failed") {
    return {
      type: "error",
      summary: apiResponse?.response,
      matches: [],
      clusters: [],
      outputText: apiResponse,
      agent: "DocumentProcessingAgent",
      intent: apiResponse?.output_text?.intent ?? undefined,
    };
  }

  // ---------- CASE SUMMARY ----------
  const caseSummary = data.caseSummary
    || data.case_summary || {};

  // ---------- COUNTS ----------
  const counts = {
    total_files:
      caseSummary.total_files
      ?? caseSummary.totalFiles
      ?? 0,
    processed:
      caseSummary.files_processed
      ?? caseSummary.filesProcessed
      ?? 0,
    acceptable:
      caseSummary.acceptable_count
      ?? caseSummary.acceptableCount
      ?? 0,
    low_quality:
      caseSummary.low_quality_count
      ?? caseSummary.lowQualityCount
      ?? 0,
    unreadable:
      caseSummary.unreadable_count
      ?? caseSummary.unreadableCount
      ?? 0,
    errors:
      caseSummary.error_count
      ?? caseSummary.errorCount
      ?? 0,
    average_quality_score:
      caseSummary.average_quality_score
      ?? caseSummary.averageQualityScore
      ?? null,
  };

  // ---------- SUMMARY ----------
  const summary = apiResponse?.response;

  // ---------- CASE LEVEL ROW ----------
  const caseId = data.caseId || data.case_id || "N/A";
  const overallStatus = caseSummary.overallStatus || caseSummary.overall_status || "Unknown";

  const meta = data.caseMetadata || data.case_metadata || {};

  const caseRow = {
    caseId: meta.caseId || caseId || "N/A",
    submitDate: meta.submitDate || "N/A",
    origin: meta.origin || "N/A",
    firstName: meta.firstName || "N/A",
    lastName: meta.lastName || "N/A",
    caseManager: meta.caseManager || "N/A",
    updatedAt: meta.updatedAt || "N/A",
    notes: meta.notes || "N/A",
    status: overallStatus,
  };

  // ---------- DOCUMENT LEVEL ROWS ----------
  const fileResults = data.fileResults || data.file_results || data.results || [];

  const documentRows = fileResults.map((file) => {
    const fileName = file.filename
      || file.fileName
      || file.file_name
      || file.documentName
      || file.document_name
      || file.blobName
      || file.blob_name
      || null;

    const fileCaseId = file.caseId || file.case_id || caseId;
    const fileUrlRaw = file.fileUrl || file.file_url || null;
    const agenticBaseUrl = getAgenticBaseUrl();
    const agenticCode = getAgenticCode();
 
    let fileUrl = null;
 
    if (agenticBaseUrl && fileName) {
      const url = `${agenticBaseUrl}/api/documents/images?case_id=${encodeURIComponent(fileCaseId)}&filename=${encodeURIComponent(fileName)}`;
      fileUrl = agenticCode ? `${url}&code=${encodeURIComponent(agenticCode)}` : url;
    } else if (agenticBaseUrl && fileUrlRaw) {
      const legacy = tryExtractLegacyDocumentParams(fileUrlRaw);
      if (legacy) {
        const url = `${agenticBaseUrl}/api/documents/images?case_id=${encodeURIComponent(legacy.caseId)}&filename=${encodeURIComponent(legacy.filename)}`;
        fileUrl = agenticCode ? `${url}&code=${encodeURIComponent(agenticCode)}` : url;
      }
    } else if (fileUrlRaw) {
      fileUrl = String(fileUrlRaw).replace(/&amp;/g, '&');
    }
    const qualityScore = file.qualityScore ?? file.quality_score ?? null;
    const blobUrl = file.blobUrl ?? file.blob_url ?? null;
    const blobName = file.blobName ?? file.blob_name ?? null;

    const structured = file.structuredData || file.structured_data || {};
    const documentType = file.documentType
      || file.document_type
      || structured.documentType
      || structured.document_type
      || "N/A";

    const issues = Array.isArray(file.issues) ? file.issues : [];
    const description = issues.length
      ? issues.map((i) => i?.description).filter(Boolean).join("; ")
      : "No issues detected";
    return {
      fileUrl,
      filename: fileName,
      documentName: file.documentName || file.document_name || fileName,
      status: file.status,
      qualityScore,
      blobUrl,
      documentId: blobName,
      documentType,
      fileSize: "N/A",
      description,
      caseId,
      structuredData: structured,
      structured_data: structured,
    };
  });

  return {
    type: "run_summary",
    summary,
    counts,
    matches: [],
    clusters: [],
    caseRow,
    documentRows,
    outputText: apiResponse,
    agent: "DocumentProcessingAgent",
    intent: apiResponse?.output_text?.intent ?? undefined,
    caseId,
  };
}
